/**
 * Os aeroportos entre os quais a pessoa precisa ESCOLHER quando diz só o nome da
 * cidade (#4157). "Dallas" é DFW ou Love Field. São dois aeroportos comerciais a
 * ~30 km um do outro, e o fornecedor não tem um "todos os aeroportos" para Dallas.
 * Escolher um pela ordem do upstream deixou o voo direto GRU–DFW fora da busca.
 *
 * Fonte única para quem precisa saber se o nome da cidade basta: o ranker de
 * candidatos do agente de voo (ambíguo → pergunta), a copy que faz a pergunta
 * (lista as opções pelo nome) e a UI de voo, que nomeia o aeroporto ao lado da
 * cidade quando ela tem mais de um (`distinguishingAirportName`). Derivada de
 * curadorias que já existem, sem tabela nova:
 *   - as opções são os aeroportos com nome de EXIBIÇÃO curado
 *     (`curatedAirports`) que servem a cidade (`cityByAirport`). Aviação geral
 *     não entra, porque ninguém a nomeia;
 *   - cidade com código METRO (`METRO_AREAS`: SAO, RIO, NYC…) não pede escolha,
 *     porque a busca pelo metro já compara todos os aeroportos;
 *   - menos de dois aeroportos nomeados também não pede escolha;
 *   - a cidade vale por QUALQUER nome que os mapas dão a um desses aeroportos
 *     (curado pt-BR, dataset, en, es): "Tokyo", "Tokio" e "Tóquio" pedem a mesma
 *     escolha. O nome popular de UM aeroporto ("Dallas-Fort Worth", que o dataset
 *     também grava como cidade do DFW) não é cidade e não pede.
 *
 * Pure (no IO): compile-time table lookups.
 */

import { cityByAirport, cityNamesByAirport } from './airport_city.js';
import { airportByPopularName, airportPopularName, curatedAirports } from './airport_names.js';
import { foldAlias } from './fold_alias.js';
import { METRO_AREAS } from './metro_city.js';

const METRO_CITIES = new Set(Object.values(METRO_AREAS).map((area) => foldAlias(area.city)));

/** Cidade dobrada → IATAs nomeados que a servem, na ordem da curadoria. */
const NAMED_BY_CITY = curatedAirports().reduce((byCity, airport) => {
  const city = cityByAirport(airport.iata);
  if (city === null) return byCity;
  const key = foldAlias(city);
  byCity.set(key, [...(byCity.get(key) ?? []), airport.iata]);
  return byCity;
}, new Map<string, string[]>());

/** Todo nome dobrado de cidade de um aeroporto nomeado → a chave canônica (a grafia
 *  de `cityByAirport`). O primeiro a gravar um nome vence. */
const CANONICAL_CITY = curatedAirports().reduce((aliases, airport) => {
  const city = cityByAirport(airport.iata);
  if (city === null) return aliases;
  for (const name of cityNamesByAirport(airport.iata)) {
    if (!aliases.has(foldAlias(name))) aliases.set(foldAlias(name), foldAlias(city));
  }
  return aliases;
}, new Map<string, string>());

/** IATAs entre os quais escolher para a cidade (`"Dallas"` → `["DFW", "DAL"]`), dita
 *  em qualquer grafia dos mapas (`"Tokyo"` → `["HND", "NRT"]`); `[]` quando o nome da
 *  cidade basta: metro, um aeroporto só, desconhecida, vazia — ou quando o termo é o
 *  nome de um aeroporto, não de uma cidade. */
export function airportChoicesOfCity(city: string | null | undefined): string[] {
  if (!city || airportByPopularName(city) !== null) return [];
  const folded = foldAlias(city);
  const key = CANONICAL_CITY.get(folded) ?? folded;
  if (!key || METRO_CITIES.has(key)) return [];
  const named = NAMED_BY_CITY.get(key) ?? [];
  return named.length > 1 ? [...named] : [];
}

/** O nome de exibição que separa o aeroporto dos IRMÃOS da cidade (`"DAL"` →
 *  `"Love Field"`, `"GRU"` → `"Guarulhos"`) — é o que a UI mostra ao lado da cidade
 *  para "Dallas" não ser só "Dallas" (#4157). Vale com ou sem metro: GRU e CGH
 *  precisam do nome mesmo com a busca no SAO. `null` quando a cidade tem um
 *  aeroporto nomeado só (FLN "Hercílio Luz" não acrescenta nada a "Florianópolis"),
 *  para código sem curadoria e para metro. */
export function distinguishingAirportName(iata: string | null | undefined): string | null {
  const code = iata?.trim().toUpperCase();
  const city = code ? cityByAirport(code) : null;
  if (!code || city === null) return null;
  const named = NAMED_BY_CITY.get(foldAlias(city)) ?? [];
  return named.length > 1 && named.includes(code) ? airportPopularName(code) : null;
}
